<script async setup>
import { defineProps, ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import FavoritesService, { FAVORITES_EVENT } from "../services/FavoritesService.js";

const props = defineProps({
  productData: {
    type: Object,
    required: true,
  },
});

/* Variations / Selection */
const variations = computed(() => (Array.isArray(props.productData?.variations) ? props.productData.variations : []));

const selectedOptions = ref({});

const normalize = (v) => String(v || "").trim().toLowerCase();

const matches = (v, selection) => {
  const vAttrs = v?.attrsRaw || {};
  for (const [tax, slug] of Object.entries(selection || {})) {
    if (!slug) continue;
    if (normalize(vAttrs[tax]) !== normalize(slug)) return false;
  }
  return true;
};

const findByActiveVariationId = () => {
  const id = Number(props.productData?.activeVariationId || 0);
  if (!id) return null;
  return variations.value.find((v) => Number(v?.id) === id) || null;
};

const findByActiveAttrsRaw = () => {
  const raw = props.productData?.activeAttrsRaw;
  if (!raw || typeof raw !== "object") return null;
  return variations.value.find((v) => matches(v, raw)) || null;
};

const readAttrsFromUrl = () => {
  if (typeof window === "undefined") return {};

  const val = new URLSearchParams(window.location.search).get("color");
  return val ? { pa_farbe: String(val).trim() } : {};
};

const readVariationIdFromUrl = () => {
  if (typeof window === "undefined") return 0;
  const val = new URLSearchParams(window.location.search).get("v");
  const id = Number(val || 0);
  return Number.isFinite(id) && id > 0 ? id : 0;

};

const getInitialSelection = () => {
  const vId = readVariationIdFromUrl();
  if (vId) {
    const v = variations.value.find((x) => Number(x?.id) === vId) || null;
    if (v?.attrsRaw) return { ...(v.attrsRaw || {}) };
  }

  const fromUrl = readAttrsFromUrl();
  if (fromUrl?.pa_farbe) {
    const v = variations.value.find((x) => matches(x, fromUrl));
    if (v?.attrsRaw) return { ...(v.attrsRaw || {}) };
  }

  const byAttrs = findByActiveAttrsRaw();
  if (byAttrs?.attrsRaw) return { ...(byAttrs.attrsRaw || {}) };

  const byId = findByActiveVariationId();
  if (byId?.attrsRaw) return { ...(byId.attrsRaw || {}) };

  const first = variations.value[0];
  if (first?.attrsRaw) return { ...(first.attrsRaw || {}) };

  return {};
};

watch(
  () => [props.productData?.activeAttrsRaw, props.productData?.activeVariationId, props.productData?.variations],
  () => {
    selectedOptions.value = getInitialSelection();
  },
  { immediate: true, deep: true }
);

const selectedVariation = computed(() => {
  const sel = selectedOptions.value || {};
  return variations.value.find((v) => matches(v, sel)) || null;
});

const addToCartId = computed(() => Number(selectedVariation.value?.id) || Number(props.productData?.id) || 0);

const activeProduct = computed(() => {
  const v = selectedVariation.value;
  if (!v) return props.productData;

  return {
    ...props.productData,
    ...v,
    price: v.price || props.productData.price,
    stock: v.stock || props.productData.stock,
    sku: v.sku || props.productData.sku,
    variantLineFull: v.variantLineFull || props.productData.variantLineFull,
    gallery: v.gallery?.length ? v.gallery : props.productData.gallery,
  };
});

const isSelected = (tax, slug) => selectedOptions.value?.[tax] === slug;

const isOptionEnabled = (tax, slug) => {
  const base = { ...(selectedOptions.value || {}) };
  delete base[tax];

  return variations.value.some((v) => {
    if (!matches(v, base)) return false;
    return (v?.attrsRaw || {})[tax] === slug;
  });
};

const optionState = (tax, slug) => {
  if (isSelected(tax, slug)) return "selected";
  return isOptionEnabled(tax, slug) ? "available" : "unavailable";
};

const selectOption = (tax, slug) => {
  const next = { ...(selectedOptions.value || {}), [tax]: slug };

  let v = variations.value.find((x) => matches(x, next));
  if (!v) v = variations.value.find((x) => (x?.attrsRaw || {})[tax] === slug) || null;

  selectedOptions.value = v?.attrsRaw ? { ...(v.attrsRaw || {}) } : next;
};

const colorState = (slug) => optionState("pa_farbe", slug);


/* Color helper */
const selectedColorSlug = computed(() => selectedOptions.value?.pa_farbe || "");
const onSelectColor = (slug) => selectOption("pa_farbe", slug);

const writeColorToUrl = (slug) => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);

  if (slug) url.searchParams.set("color", slug);
  else url.searchParams.delete("color");

  url.searchParams.delete("attribute_pa_farbe");

  window.history.replaceState({}, "", url.toString());
};

watch(selectedColorSlug, (slug) => {
  if (typeof window === "undefined") return;

  const current = new URLSearchParams(window.location.search).get("color") || "";
  if (normalize(current) === normalize(slug || "")) return;

  writeColorToUrl(slug);
});

/* Favorites */
const isFavorite = ref(false);
const isHover = ref(false);

const updateFavoriteFromService = () => {
  const id = addToCartId.value;
  isFavorite.value = id ? FavoritesService.isFavorite(id) : false;
};

const onFavoritesChanged = () => {
  updateFavoriteFromService();
};

watch(addToCartId, updateFavoriteFromService, { immediate: true });

onMounted(() => {
  if (typeof window !== "undefined") {
    window.addEventListener(FAVORITES_EVENT, onFavoritesChanged);
  }
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener(FAVORITES_EVENT, onFavoritesChanged);
  }
});

/* Slider re-init */
const sliderLoaded = ref(false);

watch(
  () => activeProduct.value?.gallery,
  async () => {
    sliderLoaded.value = false;
    await nextTick();
    sliderLoaded.value = true;
  },
  { deep: true, immediate: true }
);

/* Quantity */
const currentValue = ref(1);

const increaseValue = () => {
  currentValue.value = currentValue.value + 1;
};

const decreaseValue = () => {
  if (currentValue.value > 1) currentValue.value = currentValue.value - 1;
};

/* Accordion data */
const dimensionsSpecs = computed(() => (Array.isArray(props.productData?.dimensions) ? props.productData.dimensions : []));
const midIndex = computed(() => Math.ceil(dimensionsSpecs.value.length / 2));
const leftCol = computed(() => dimensionsSpecs.value.slice(0, midIndex.value));
const rightCol = computed(() => dimensionsSpecs.value.slice(midIndex.value));
const techSpecs = ref([]);
</script>

<template>
    <div class="flex flex-col mini:flex-row gap-8">
        <div class=" mini:w-[49%] grow-1 shrink-0">
            <kd-interactive-slider
                v-if="Array.isArray(activeProduct.gallery) && activeProduct.gallery.length"
                :key="addToCartId + '-' + (activeProduct.gallery?.[0]?.id ?? 0)"
                :element-id="'product-' + addToCartId"
                :slides="activeProduct.gallery"
                :current-slide="1"
                :data-loaded="sliderLoaded"
                class="transition-opacity duration-500 ease-in-out opacity-0 data-[loaded=true]:opacity-100"
            />
            <!-- eslint-disable vue/no-v-html -->
            <div v-if="productData.xentralDescription" v-html="productData.xentralDescription" class="x-rt hidden mini:block" />
            <!-- eslint-enable -->
        </div>
        <div class="flex flex-col">
            <div class="flex flex-col gap-3">
                <div class="flex justify-between items-center relative">
                    <img 
                        v-if="props.productData.brand?.logo.url" 
                        :src="props.productData.brand.logo.url"
                        :alt="props.productData.brand.logo.alt" 
                        :srcset="props.productData.brand.logo.srcset"
                        :title="props.productData.brand.name" 
                        class="w-25.5 object-contain"
                    >
                                
                    <div
                        class="group absolute right-0 top-0"
                        @mouseenter="isHover = true"
                        @mouseleave="isHover = false"
                    >
                        <kd-icon
                            icon="heart"
                            :filled="isFavorite || isHover"
                            size="30"
                            class="transition-transform duration-200 cursor-pointer hover:-translate-y-0.5"
                            @click="isFavorite = FavoritesService.toggle(addToCartId)"
                        />
                    </div>
                </div>
                <h1 class="font-semibold uppercase text-base mini:text-lg mini:max-w-[80%]">
                    {{ props.productData.title }}
                </h1>
                <!-- Preise -->
                <div class="text-lg">
                    <span v-if="activeProduct.price?.regularText" class="line-through text-black/60 decoration-black decoration-2 content-center mr-3">
                        {{ activeProduct.price?.regularText || '' }}
                    </span>
                    <span class="font-bold">
                        {{ activeProduct.price?.saleText || '' }}
                    </span>
                    <span class="font-bold text-base ml-1">{{ props.productData.labels.vatText }}</span>
                </div>

                <p v-if="activeProduct.variantLineFull" class="text-sm">
                    <span class="font-bold">{{ props.productData.labels.variant }}:</span>
                    <span class="ml-2">{{ activeProduct.variantLineFull }}</span>
                </p>
                <!-- Farbwahl -->
                <div
                    v-if="Array.isArray(props.productData.colors) && props.productData.colors.length"
                    class="flex items-start gap-2"
                >
                    <span class="text-sm font-bold whitespace-nowrap mt-0.5 shrink-0">
                        {{ props.productData.labels.colorLabel }}:
                    </span>
                
                    <div class="flex flex-wrap gap-2 max-w-75">
                        <a
                            v-for="c in props.productData.colors"
                            :key="c.slug"
                            :href="c.url"
                            :title="c.name"
                            class="p-0.5 rounded-full transition-all duration-200 flex items-center justify-center"
                            :class="{
                                'border border-black': colorState(c.slug) === 'selected',
                                'border border-gray-300 hover:border-black': colorState(c.slug) === 'available',
                                'border border-gray-300 opacity-40': colorState(c.slug) === 'unavailable'
                            }"
                            aria-current="page"
                            :aria-pressed="String(c.slug === selectedColorSlug)"
                            @click.prevent="onSelectColor(c.slug)"
                        >
                            <span
                                class="block size-4 rounded-full"
                                :style="{
                                    background: c.hex2
                                        ? `linear-gradient(135deg, ${c.hex} 50%, ${c.hex2} 50%)`
                                        : c.hex
                                }"
                            ></span>
                        </a>
                    </div>
                </div>
                <!-- Variant Attribute -->
                <div
                    v-if="Array.isArray(props.productData.variationAttributes) && props.productData.variationAttributes.length" 
                    class="flex flex-col gap-3"
                >
                    <div
                        v-for="attr in props.productData.variationAttributes"
                        :key="attr.tax"
                        class="flex items-start gap-2"
                    >
                        <div class="text-sm font-bold whitespace-nowrap mt-0.5">
                            {{ attr.label }}:
                        </div>
                        <div class="flex flex-wrap gap-2">
                            <button
                                v-for="opt in attr.options"
                                :key="attr.tax + '-' + opt.slug"
                                type="button"
                                class="px-3 py-0.5 rounded-full border text-sm transition"
                                :class="{
                                    'bg-black text-white border-black': optionState(attr.tax, opt.slug) === 'selected',
                                    'border-black hover:border-black': optionState(attr.tax, opt.slug) === 'available',
                                    'border-black opacity-40': optionState(attr.tax, opt.slug) === 'unavailable'
                                }"
                                @click="selectOption(attr.tax, opt.slug)"
                            >
                                {{ opt.name }}
                            </button>
                        </div>
                    </div>
                </div>
                <!-- Lagerbestand -->
                <div class="flex items-start text-sm gap-1.5">
                    <div class="flex items-center gap-1.5 flex-nowrap shrink-0">
                        <span class="font-semibold mr-1 whitespace-nowrap">
                            {{ props.productData.labels.stockStatusLabel }}
                        </span>
                        <kd-icon-package size="17" fill="black" />:
                        <span 
                            :class="{
                                'inline-block rounded-full size-2': true,
                                'bg-ts-gray': activeProduct.stock?.status !== 'instock' && activeProduct.stock?.status !== 'onbackorder',
                                'bg-ts-green': activeProduct.stock?.status === 'instock',
                                'bg-ts-orange': activeProduct.stock?.status === 'onbackorder'
                            }"
                        />
                    </div>
                    <span class="flex-1 whitespace-normal leading-snug">
                        {{ activeProduct.stock?.stockText || (props.productData.labels?.stockStatus?.unknown ?? '') }}
                    </span>
                </div>
                <!-- Lieferung -->
                <div class="flex items-start text-sm gap-1.5">
                    <div class="flex items-center gap-1.5 flex-nowrap shrink-0">
                        <span class="font-semibold mr-1 whitespace-nowrap">
                            {{ props.productData.labels.deliveryLabel }}
                        </span>
                        <kd-icon-delivery size="19" fill="black" />:
                        <span 
                            :class="{
                                'inline-block rounded-full size-2': true,
                                'bg-ts-gray': activeProduct.stock?.status !== 'instock' && activeProduct.stock?.status !== 'onbackorder',
                                'bg-ts-green': activeProduct.stock?.status === 'instock',
                                'bg-ts-orange': activeProduct.stock?.status === 'onbackorder'
                            }"
                        />
                    </div>
                    <span class="flex-1 whitespace-normal leading-snug">
                        {{ activeProduct.stock?.deliveryText || (props.productData.labels?.deliveryStatus?.unknown?.availability + ', ' + props.productData.labels?.deliveryStatus?.unknown?.lead) || '' }}
                    </span>
                </div>
                <!-- Artikelnummer -->
                <div class="flex items-center gap-1 text-sm">
                    <span class="font-bold">{{ props.productData.labels.skuLabel }}:</span>
                    <span>{{ activeProduct.sku || '' }}</span>
                </div>
                <!-- Warenkorb -->
                <form 
                    class="js-add-to-cart-form" 
                    method="post" 
                    @submit.prevent
                    :data-product-id="addToCartId"
                    :class="{ 'mt-0': props.productData.isVariable }"
                >
                    <div class="flex flex-row flex-nowrap items-center gap-3">
                        <div class="flex flex-row flex-nowrap items-center border border-black rounded-full py-1 px-2">
                            <div class="cursor-pointer" @click="decreaseValue">
                                <svg 
                                    width="10" 
                                    height="10" 
                                    viewBox="0 0 10 2" 
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M0.884563 2.00012H18.1155C18.5939 2.00012 19 1.54786 19 1.0152C19 0.472484 18.5939 0.0302734 18.1155 0.0302734H0.884563C0.406177 0.0302734 0 0.472484 0 1.0152C0 1.54786 0.406177 2.00012 0.884563 2.00012Z"
                                        fill="black" 
                                    />
                                </svg>
                            </div>
                            <div>
                                <input 
                                    v-model="currentValue" 
                                    name="quantity" 
                                    type="text" 
                                    readonly="readonly"
                                    class="border-none text-center w-6 p-0"
                                    :aria-label="props.productData.labels.amountField"
                                >
                            </div>
                            <div class="cursor-pointer" @click="increaseValue">
                                <svg 
                                    width="10" 
                                    height="10" 
                                    viewBox="0 0 19 19" 
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M0 9.49548C0 9.97341 0.406177 10.3702 0.884563 10.3702H8.61998V18.0982C8.61998 18.5762 9.01712 18.9819 9.49551 18.9819C9.98292 18.9819 10.3801 18.5762 10.3801 18.0982V10.3702H18.1155C18.5939 10.3702 19 9.97341 19 9.49548C19 9.00853 18.5939 8.61177 18.1155 8.61177H10.3801V0.883721C10.3801 0.405791 9.98292 0 9.49551 0C9.01712 0 8.61998 0.405791 8.61998 0.883721V8.61177H0.884563C0.406177 8.61177 0 9.00853 0 9.49548Z"
                                        fill="black" 
                                    />
                                </svg>
                            </div>
                        </div>

                        <kd-button 
                            icon="cart" 
                            variant="black" 
                            type="submit" 
                            name="add-to-cart" 
                            :value="addToCartId"
                        >
                            {{ props.productData.labels.addToCart }}
                        </kd-button>
                    </div>
                </form>
                <!-- Highlights -->
                <div
                    v-if="Array.isArray(props.productData.highlights) && props.productData.highlights.length"
                    class="text-sm"
                >
                    <div class="font-bold mb-2">
                        {{ props.productData.labels.highlightsLabel }}:
                    </div>
                
                    <ul class="space-y-2.5">
                        <li
                            v-for="row in props.productData.highlights"
                            :key="row.label + '-' + row.value"
                            class="flex items-start gap-1"
                        >
                            <kd-icon-checkmark
                                size="18"
                                fill="black"
                                class="shrink-0 mt-0.5"
                            />
                            <span class="text-sm">
                                {{ row.value }}
                            </span>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="flex flex-col mt-6">
                <div class="border-t-2 border-black ">
                    <kd-accordion 
                        id="dimensions-data" 
                        :title="props.productData.labels.dimensionsTitle"
                        :count="String(dimensionsSpecs.length)"
                    >
                        <div class="relative grid grid-cols-1 xlbook:grid-cols-2 gap-x-10 text-sm">                            
                            <div class="hidden xlbook:block absolute inset-y-0 left-1/2 w-px bg-black"></div>
                            <div>
                                <div
                                    v-for="row in leftCol"
                                    :key="'left-' + row.label"
                                    class="py-2 flex justify-between"
                                >
                                    <span class="font-bold">{{ row.label }}</span>
                                    <span>{{ row.value }}</span>
                                </div>
                            </div>
                        
                            <!-- RECHTER BLOCK -->
                            <div>
                                <div
                                    v-for="row in rightCol"
                                    :key="'right-' + row.label"
                                    class="py-2 flex justify-between"
                                >
                                    <span class="font-bold">{{ row.label }}</span>
                                    <span>{{ row.value }}</span>
                                </div>
                            </div>
                        </div>
                    </kd-accordion>
                </div>
                <kd-accordion 
                    id="tech-data" 
                    :title="props.productData.labels.techDataTitle"
                    :count="String(techSpecs.length)"
                >                                                        
                    <div v-if="props.productData.safetyPdf" class="text-sm space-y-2">
                        <!-- PDF Link -->
                        <a
                            v-if="props.productData.safetyPdf.url"
                            :href="props.productData.safetyPdf.url"
                            target="_blank"
                            rel="noopener"
                            class="underline block"
                        >
                            {{ props.productData.safetyPdf.label || props.productData.safetyPdf.filename || props.productData.safetyPdf.title }}
                        </a>
                    
                        <div
                            v-if="props.productData.safetyPdf.text"
                            v-html="props.productData.safetyPdf.text"
                            class="prose prose-sm"
                        ></div>
                    </div>
                </kd-accordion>
                <kd-accordion 
                    id="notes-data" 
                    :title="props.productData.labels.notesTitle"
                    :count="props.productData.notesHtml ? '1' : '0'"
                >
                    <div v-if="props.productData.notesHtml">
                        <!-- eslint-disable vue/no-v-html -->
                        <div class="text-sm" v-html="props.productData.notesHtml"></div>
                        <!-- eslint-enable -->
                    </div>
                </kd-accordion>
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div v-if="productData.xentralDescription" v-html="productData.xentralDescription" class="x-rt mini:hidden" />
            <!-- eslint-enable -->
        </div>
    </div>
</template>

<style scoped>

.x-rt * {
    all: revert;
}

.x-rt {
    font-family: inherit;
}
</style>